import { executeFinanceCommand } from "@/features/finance/lib/retry-safe-command";
import type { Json } from "@/db/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import {
  applyFinanceDocumentSearch,
  applyFinanceListSortedPage,
  archiveFinanceDocument,
  FINANCE_DOCUMENT_LINE_CORE,
  type FinanceDocumentChildDescriptor,
  financeDocumentHeaderSelect,
  financeDocumentQuery,
  readFinanceDocumentChildren,
  resolveFinanceListPage,
  withAppWriteOrigin,
} from "@/lib/db/finance-documents";
import { DOCUMENT_TOTAL_FIELDS, fetchListTotals } from "@/lib/db/list-totals";
import { ACCOUNT_SELECT, PRODUCT_SELECT } from "@/lib/db/selects";
import { deleteCustomerPayment as deleteCustomerPaymentImpl } from "@/lib/payment-delete";
import { normalizePaymentSplitResult } from "@/lib/payment-split";
import { InvoiceCreateCommandError } from "@/features/invoices/lib/invoice-create-failure";
import {
  collectReversedApplicationIds,
  markReversedApplications,
} from "@/lib/credit-application-balances";
import { isPaymentMethod } from "@/lib/types/finance";
import {
  cast,
  firstRecord,
  getOptionalString,
  normalizeDateOnlyInput,
  toNumber,
  toPostgrestDayUntil,
  toPostgrestIlikeContains,
  toRangeEndDay,
} from "@/lib/utils/query-helpers";
import type {
  CreateCustomerPaymentResult,
  Account,
  CreateInvoicePayload,
  CreateInvoiceResult,
  CreatePaymentPayload,
  CreditApplication,
  CustomerPayment,
  Invoice,
  InvoiceFilters,
  InvoiceLine,
  Product,
  ProductKanbanStatus,
  UpdateInvoicePayload,
} from "../types";

const backend = getBackendClient();
export const deleteCustomerPayment = deleteCustomerPaymentImpl;

const INVOICE_LINE_SELECT = `
  ${FINANCE_DOCUMENT_LINE_CORE},
  gl_products(cost, sku, vendor_product_name, product_name, purchase_order_uid, gl_accounts(uid))
`;

const CREDIT_APPLICATION_SELECT = `
  id, credit_id, invoice_id, estimate_id, amount_applied, is_reversal,
  applied_at, reverses_application_id,
  gl_customer_credits!gl_credit_applications_credit_id_fkey (
    id, uid, amount, date, method, reference, account_id
  )
`;

interface InvoiceLineScope {
  id: string;
  invoice_id: string;
}

/**
 * The filters the invoice list narrows by, applied in one place.
 *
 * `getInvoices` and the export's paged walk both go through here, so the file a
 * user downloads is the set the table is showing rather than a second, slightly
 * different query. The totals RPC mirrors this same set server-side.
 *
 * Search matches `search_text`, a computed column defined in
 * `migrations/20260726160000_add-list-search-and-totals.sql`: uid, notes,
 * status, the invoice's own amounts, the customer's name and uid, and the
 * reference on every payment against it. Previously it reached uid and notes
 * only, so an invoice could not be found by amount or by payment reference —
 * the two things a worker chasing a payment actually has in hand.
 */
function applyInvoiceListFilters<T>(
  query: QueryBuilder<T>,
  filters: InvoiceFilters,
): QueryBuilder<T> {
  let next = applyFinanceDocumentSearch(query, filters.search);

  if (filters.account_id) {
    next = next.eq("account_id", filters.account_id);
  }
  if (filters.statuses?.length) {
    next = next.in("payment_status", filters.statuses);
  }
  if (filters.date_from) {
    next = next.gte("date", filters.date_from);
  }
  if (filters.date_to) {
    next = next.lt("date", toPostgrestDayUntil(filters.date_to));
  }

  return next;
}

function normalizePaymentMethod(source?: { method?: unknown } | null) {
  const candidate = source?.method;
  return typeof candidate === "string" && isPaymentMethod(candidate) ? candidate : "Cash";
}

function buildInvoiceLineRpcPayload(lines: CreateInvoicePayload["lines"]): Json[] {
  return lines.map((line) => ({
    description: line.description ?? null,
    product_id: line.product_id ?? null,
    product_name: line.product_name ?? null,
    quantity: line.quantity,
    selling_price: line.selling_price,
    unit_cost: line.unit_cost ?? null,
  }));
}

function buildCreditApplicationRpcPayload(
  applications: CreateInvoicePayload["credit_applications"],
): Json[] {
  return (applications ?? []).map((application) => ({
    amount: application.amount,
    credit_id: application.credit_id,
  }));
}

function buildInitialPaymentRpcPayload(
  payment: CreateInvoicePayload["initial_payment"],
): Json | null {
  if (!payment) {
    return null;
  }

  return {
    amount: payment.amount,
    date: payment.date ?? null,
    method: normalizePaymentMethod(payment),
    reference: payment.reference ?? null,
  };
}

function getCreatedInvoiceId(normalizedCreate: Record<string, unknown> | null) {
  if (typeof normalizedCreate?.invoice_id === "string") {
    return normalizedCreate.invoice_id;
  }
  if (typeof normalizedCreate?.document_id === "string") {
    return normalizedCreate.document_id;
  }
  return null;
}

function buildCreateInvoiceResult(
  invoice: unknown,
  normalizedCreate: Record<string, unknown> | null,
): CreateInvoiceResult {
  return {
    credit_applications: Array.isArray(normalizedCreate?.credit_applications)
      ? normalizedCreate.credit_applications
      : [],
    initial_payment: normalizedCreate?.initial_payment ?? null,
    invoice: cast<Invoice>(invoice),
    merged_into_existing:
      normalizedCreate?.merged_into_existing === true ||
      normalizedCreate?.merged_into_existing === "true",
  };
}

/**
 * Lists invoices with pagination, account, status, date, and text filters.
 *
 * Ordering is server-side and caller-driven: the list writes the picked column
 * into the URL and hands it back here, so a sort spans every page instead of
 * reordering the twenty rows already loaded. `orderListPage` closes it with the
 * unique tiebreaker every paged read needs — 221 invoice dates are shared by
 * more than one invoice, so `date` alone cannot decide a page boundary.
 */
export async function getInvoices(filters: InvoiceFilters): Promise<Invoice[]> {
  let query = applyFinanceListSortedPage(
    financeDocumentQuery("invoice"),
    {
      ascending: filters.sort_direction === "asc",
      column: filters.sort_field ?? "date",
    },
    resolveFinanceListPage(filters, 20),
  );

  query = applyInvoiceListFilters(query, filters);

  // A converted-and-undone invoice is a voided shell, not a receivable: it
  // stays in the database for the ledger trail but leaves every active list.
  query = query.is("voided_at", null);

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }
  return cast<Invoice[]>(data || []);
}

/**
 * The invoice list's filters as RPC arguments, written once.
 *
 * The totals footer, the bulk confirm figures and the bulk write all have to
 * describe the same set of invoices. They are three separate calls, so the
 * filter mapping is the one thing that could quietly differ between them — and
 * a confirm dialog that counts a different set than the write settles is the
 * exact failure a bulk financial action must not have. There is one mapping.
 *
 * The footer takes the same mapping with one deliberate exception, the end
 * bound; see {@link getInvoiceTotals}.
 */
function invoiceFilterRpcArgs(filters: InvoiceFilters): Record<string, unknown> {
  return {
    p_account_id: filters.account_id || null,
    p_date_from: filters.date_from || null,
    p_date_to: filters.date_to || null,
    p_search: filters.search?.trim() || null,
    p_statuses: filters.statuses?.length ? filters.statuses : null,
  };
}

/**
 * The receivables list also asks how many of those invoices are still owed, so
 * the metric cards above the table read the same aggregate as the strip below
 * it instead of counting the page they can see.
 */
const INVOICE_TOTAL_FIELDS = [...DOCUMENT_TOTAL_FIELDS, "open_count"] as const;

export type InvoiceListTotals = Record<(typeof INVOICE_TOTAL_FIELDS)[number], number>;

/**
 * Count and money totals for the whole filtered set, from the server.
 *
 * Deliberately not derived from the rows: `getInvoices` returns one page, so a
 * total computed here would describe twenty invoices while the table above it
 * claims to be filtered to two hundred. The RPC takes the same filters this
 * module applies, so the two describe the same set by construction.
 */
export async function getInvoiceTotals(filters: InvoiceFilters): Promise<InvoiceListTotals> {
  return fetchListTotals(
    "invoice_list_totals",
    {
      ...invoiceFilterRpcArgs(filters),
      // The footer closes on the whole end day, as the list does, so it gets the
      // day the list reads from `date_to`. The bulk preview and write keep the
      // raw bound (`bulkTargetRpcArgs`): "settle all matching" keeps its
      // existing end-day rule, so its selection does not follow the list.
      p_date_to: filters.date_to ? toRangeEndDay(filters.date_to) : null,
    },
    INVOICE_TOTAL_FIELDS,
  );
}

/**
 * Lists products available to invoice lines with optional status filters.
 * `exclude_status` only takes effect when `status` is not set, so callers
 * can default to hiding a status (e.g. sold inventory) while still letting
 * an explicit status filter select it.
 */
export async function getProducts(params?: {
  search?: string;
  status?: ProductKanbanStatus;
  exclude_status?: ProductKanbanStatus;
  purchase_order_id?: string;
  account_id?: string;
  category?: string;
  date_from?: string;
  date_to?: string;
  in_stock_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<Product[]> {
  let query = applyFinanceListSortedPage(
    backend.data.from("gl_products").select(PRODUCT_SELECT).eq("is_deleted", false),
    { ascending: false, column: "created_at" },
    resolveFinanceListPage(params, 100),
  );

  if (params?.search?.trim()) {
    const search = toPostgrestIlikeContains(params.search.trim());
    query = query.or(
      `product_name.ilike.${search},vendor_product_name.ilike.${search},sku.ilike.${search}`,
    );
  }
  if (params?.purchase_order_id) {
    query = query.eq("purchase_order_id", params.purchase_order_id);
  }
  if (params?.account_id) {
    query = query.eq("account_id", params.account_id);
  }
  if (params?.category) {
    query = query.eq("category", params.category);
  }
  if (params?.status) {
    query = query.eq("kanban_status", params.status);
  } else if (params?.exclude_status) {
    query = query.neq("kanban_status", params.exclude_status);
  }
  if (params?.date_from) {
    query = query.gte("date", params.date_from);
  }
  if (params?.date_to) {
    query = query.lt("date", toPostgrestDayUntil(params.date_to));
  }
  if (params?.in_stock_only) {
    query = query.gt("quantity", 0);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }
  return cast<Product[]>(data || []);
}

/**
 * The shape PostgREST accepts for a `uuid` literal.
 *
 * A recovered draft's ids come from `sessionStorage`, which the operator can
 * edit. PostgREST rejects the whole request (`22P02`) when a `uuid` filter
 * carries a non-UUID value, and the wizard reads that as "reference lookup
 * unavailable" — so one malformed id would block recovery of an otherwise
 * valid draft instead of discarding it. Dropping the unqueryable value lets
 * the lookup simply find nothing, which is the same outcome as an unavailable
 * record: the draft is discarded. Matches the shape `src/lib/assistant/text-stream.ts`
 * accepts for server-issued resource ids; deliberately not the v4-only pattern
 * `useIdempotentCommandKey` applies to keys this app generates itself.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function queryableIds(ids: string[]): string[] {
  return ids.filter((id) => UUID_PATTERN.test(id));
}

/**
 * Re-reads persisted invoice-draft references through current RLS policies.
 *
 * The account lookup also requires `is_customer`, matching the customer picker
 * the invoice wizard restores into: an account that keeps its rows but loses
 * the customer role must not come back as the draft's customer.
 */
export async function getInvoiceDraftReferences({
  accountId,
  productIds,
}: {
  accountId: string;
  productIds: string[];
}): Promise<{ accounts: Account[]; products: Product[] }> {
  const [queryableAccountId] = queryableIds([accountId]);
  const queryableProductIds = queryableIds(productIds);
  const [accountResult, productResult] = await Promise.all([
    queryableAccountId
      ? backend.data
          .from("gl_accounts")
          .select(ACCOUNT_SELECT)
          .in("id", [queryableAccountId])
          .eq("is_customer", true)
      : Promise.resolve({ data: [], error: null }),
    queryableProductIds.length > 0
      ? backend.data
          .from("gl_products")
          .select(PRODUCT_SELECT)
          .in("id", queryableProductIds)
          .eq("is_deleted", false)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (accountResult.error) {
    throw new Error(accountResult.error.message);
  }
  if (productResult.error) {
    throw new Error(productResult.error.message);
  }
  return {
    accounts: cast<Account[]>(accountResult.data || []),
    products: cast<Product[]>(productResult.data || []),
  };
}

/** Lists a paginated slice of invoice lines for one invoice. */
export async function getInvoiceLines(params: {
  invoice_id: string;
  limit?: number;
  offset?: number;
}): Promise<InvoiceLine[]> {
  const { data, error } = await applyFinanceListSortedPage(
    backend.data
      .from("gl_invoice_lines")
      .select(INVOICE_LINE_SELECT)
      .eq("invoice_id", params.invoice_id),
    { ascending: true, column: "created_at" },
    resolveFinanceListPage(params, 100),
  );

  if (error) {
    throw new Error(error.message);
  }
  return cast<InvoiceLine[]>(data || []);
}

/** Creates one invoice line; DB orchestrator triggers recalc invoice totals. */
export async function createInvoiceLine(payload: {
  description?: string | null;
  invoice_id: string;
  product_id?: string | null;
  product_name: string;
  quantity: number;
  selling_price: number;
  unit_cost?: number | null;
}): Promise<InvoiceLine> {
  const { data, error } = await backend.data
    .from("gl_invoice_lines")
    .insert([
      withAppWriteOrigin({
        description: payload.description ?? null,
        invoice_id: payload.invoice_id,
        product_id: payload.product_id ?? null,
        product_name: payload.product_name,
        quantity: payload.quantity,
        selling_price: payload.selling_price,
        unit_cost: payload.unit_cost ?? null,
      }),
    ])
    .select(INVOICE_LINE_SELECT)
    .single();

  if (error) {
    throw new Error(error.message);
  }
  return cast<InvoiceLine>(data);
}

/** Updates one invoice line; line_total is DB-generated from quantity * selling_price. */
export async function updateInvoiceLine(
  scope: InvoiceLineScope,
  updates: {
    description?: string | null;
    product_name?: string;
    quantity: number;
    selling_price: number;
    unit_cost?: number | null;
  },
): Promise<InvoiceLine> {
  const { data, error } = await backend.data
    .from("gl_invoice_lines")
    .update(
      withAppWriteOrigin({
        ...(updates.description === undefined ? {} : { description: updates.description }),
        ...(updates.product_name === undefined ? {} : { product_name: updates.product_name }),
        quantity: updates.quantity,
        selling_price: updates.selling_price,
        ...(updates.unit_cost === undefined ? {} : { unit_cost: updates.unit_cost }),
      }),
    )
    .eq("id", scope.id)
    .eq("invoice_id", scope.invoice_id)
    .select(INVOICE_LINE_SELECT)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Invoice line not found.");
  }
  return cast<InvoiceLine>(data);
}

/** Deletes one invoice line; DB orchestrator triggers recalc invoice totals. */
export async function deleteInvoiceLine(scope: InvoiceLineScope): Promise<void> {
  const { data, error } = await backend.data
    .from("gl_invoice_lines")
    .delete()
    .eq("id", scope.id)
    .eq("invoice_id", scope.invoice_id)
    .select("id")
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Invoice line not found.");
  }
}

/**
 * Creates or merges an invoice for the requested account and date.
 *
 * Binds the call to an idempotency key so a lost response followed by a
 * caller-driven retry cannot re-apply lines, credits, or the initial payment.
 * Pass `payload.idempotency_key` back in to retry the *same* logical submit;
 * omit it (the common case) to let this generate a fresh key for a new
 * submit.
 */
export async function createInvoice(payload: CreateInvoicePayload): Promise<CreateInvoiceResult> {
  const targetDate = normalizeDateOnlyInput(payload.date ?? null);

  const { data: createResult, error: createError } = await backend.data.rpc(
    "xan_create_invoice_with_payload",
    {
      p_account_id: payload.account_id,
      p_date: targetDate,
      p_notes: payload.notes || null,
      p_lines: buildInvoiceLineRpcPayload(payload.lines),
      p_credit_applications: buildCreditApplicationRpcPayload(payload.credit_applications),
      p_initial_payment: buildInitialPaymentRpcPayload(payload.initial_payment),
      p_idempotency_key: payload.idempotency_key ?? crypto.randomUUID(),
    },
  );

  if (createError) {
    // The code decides whether the command definitively did not commit: the
    // controller only asks for reconciliation when the outcome is unknown, so
    // dropping it here would make every rejection look ambiguous.
    throw new InvoiceCreateCommandError(createError.message, {
      code: createError.code,
      statusCode: createError.statusCode,
    });
  }

  const normalizedCreate = firstRecord(createResult);
  const invoiceId = getCreatedInvoiceId(normalizedCreate);
  if (!invoiceId) {
    throw new Error("Unable to resolve invoice.");
  }

  const { data: invoice, error: invoiceError } = await financeDocumentQuery("invoice")
    .eq("id", invoiceId)
    .single();

  if (invoiceError) {
    throw new Error(invoiceError.message);
  }

  return buildCreateInvoiceResult(invoice, normalizedCreate);
}

/**
 * Child relations of the invoice detail read, resolved by the shared port.
 * The tables are invoice-specific; the fan-out, scoping, and first-error
 * semantics are not.
 */
const INVOICE_DETAIL_CHILDREN: readonly FinanceDocumentChildDescriptor[] = [
  {
    from: () => backend.data.from("gl_invoice_lines").select(INVOICE_LINE_SELECT),
    order: { ascending: true, column: "created_at" },
    parentColumn: "invoice_id",
  },
  {
    from: () =>
      backend.data.from("gl_customer_payments").select("id, amount, date, method, reference, uid"),
    order: { ascending: false, column: "date" },
    parentColumn: "invoice_id",
  },
  {
    from: () => backend.data.from("gl_credit_applications").select(CREDIT_APPLICATION_SELECT),
    order: { ascending: false, column: "applied_at" },
    parentColumn: "invoice_id",
  },
  {
    from: () =>
      backend.data
        .from("gl_estimate_conversions")
        .select(
          "id, estimate_id, invoice_id, conversion_type, transfer_batch_id, line_mappings, credit_transfer_mappings, undo_credit_transfer_mappings, converted_at, undone_at, undo_reason",
        ),
    order: { ascending: false, column: "converted_at" },
    parentColumn: "invoice_id",
  },
];

/** Fetches one invoice with lines, payments, and credit applications. */
export async function getInvoice(id: string): Promise<
  Invoice & {
    lines: InvoiceLine[];
    payments: CustomerPayment[];
    credit_applications: CreditApplication[];
    conversions: NonNullable<Invoice["conversions"]>;
  }
> {
  const headerPromise = backend.data
    .from("gl_invoices")
    .select("*, gl_accounts!gl_invoices_account_id_fkey ( id, name, uid, type )")
    .eq("id", id)
    .is("voided_at", null)
    .maybeSingle();
  const childrenPromise = readFinanceDocumentChildren(id, INVOICE_DETAIL_CHILDREN);
  const { data: invoice, error: invoiceError } = await headerPromise;

  if (invoiceError) {
    void childrenPromise.catch(() => undefined);
    throw new Error(invoiceError.message);
  }
  if (!invoice) {
    void childrenPromise.catch(() => undefined);
    throw new Error("Invoice not found.");
  }

  const [lines, payments, applicationRows, conversions] = await childrenPromise;

  const applications = cast<CreditApplication[]>(applicationRows ?? []);

  return {
    ...cast<Invoice>(invoice),
    lines: cast<InvoiceLine[]>(lines ?? []),
    payments: cast<CustomerPayment[]>(payments ?? []),
    credit_applications: markReversedApplications(
      applications,
      collectReversedApplicationIds(applications),
    ),
    conversions: cast<NonNullable<Invoice["conversions"]>>(conversions ?? []),
  };
}

/** Mirrors xan_create_or_attach_document, including its status-independent ordering. */
export async function getInvoicePreviewMergeTarget(
  accountId: string,
  date: string,
): Promise<Invoice | null> {
  const { data, error } = await backend.data
    .from("gl_invoices")
    .select("*")
    .eq("account_id", accountId)
    .eq("date", date)
    .order("created_at", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false })
    .range(0, 0);
  if (error) throw new Error(error.message);
  const invoice = cast<Invoice[]>(data ?? [])[0];
  if (!invoice) return null;
  const [lines] = await readFinanceDocumentChildren(
    invoice.id,
    INVOICE_DETAIL_CHILDREN.slice(0, 1),
  );
  return { ...invoice, lines: cast<InvoiceLine[]>(lines ?? []) };
}

/** Updates editable invoice metadata. */
export async function updateInvoice(id: string, payload: UpdateInvoicePayload): Promise<Invoice> {
  const updates: Record<string, unknown> = {};

  if (payload.date !== undefined) {
    updates.date = normalizeDateOnlyInput(payload.date);
  }
  if (payload.notes !== undefined) {
    updates.notes = payload.notes;
  }

  const { data, error } = await backend.data
    .from("gl_invoices")
    .update(withAppWriteOrigin(updates))
    .eq("id", id)
    .select(financeDocumentHeaderSelect("invoice"))
    .maybeSingle();

  if (error) {
    throw new Error("Failed to update invoice.");
  }
  if (!data) {
    throw new Error("Invoice not found.");
  }

  return cast<Invoice>(data);
}

/** Soft-deletes an invoice through the backend archive RPC. */
export function deleteInvoice(id: string): Promise<{ success: boolean; archive_id: string }> {
  return archiveFinanceDocument("invoice", id);
}

/** Applies a customer payment and returns the normalized split result. */
export async function createCustomerPayment(
  payload: CreatePaymentPayload,
): Promise<CreateCustomerPaymentResult> {
  const { data: invoice, error: invoiceError } = await backend.data
    .from("gl_invoices")
    .select("id, account_id, balance")
    .eq("id", payload.invoice_id)
    .maybeSingle();

  if (invoiceError) {
    throw new Error(invoiceError.message);
  }
  if (!invoice) {
    throw new Error("Invoice not found.");
  }
  if (!invoice.account_id) {
    throw new Error("Invoice account was not found.");
  }
  if (invoice.account_id !== payload.account_id) {
    throw new Error("Payment account_id does not match the invoice account.");
  }

  const requestedAmount = payload.amount;

  const command = await executeFinanceCommand("customer_payment", {
    document_id: payload.invoice_id,
    amount: payload.amount,
    date: normalizeDateOnlyInput(payload.date ?? null),
    method: normalizePaymentMethod(payload),
    reference: payload.reference || null,
  });

  const normalizedSplit = normalizePaymentSplitResult(command.result, {
    requestedAmount,
    invoiceId: payload.invoice_id,
  });
  command.acknowledge();

  return {
    split: normalizedSplit.split,
  };
}

/**
 * The set of invoices a bulk action covers.
 *
 * `invoiceIds` non-null means the rows ticked on the page; `null` means "every
 * invoice these filters match", resolved server-side rather than by shipping
 * ids up from a page the operator has only partly seen. The server takes the
 * same two modes, so this type is the whole contract.
 */
export interface InvoiceBulkTarget {
  filters: InvoiceFilters;
  invoiceIds: string[] | null;
}

export const INVOICE_BULK_MARK_PAID_FIELDS = [
  "row_count",
  "settle_count",
  "settle_balance",
  "settled_already_count",
] as const;

export type InvoiceBulkMarkPaidPreview = Record<
  (typeof INVOICE_BULK_MARK_PAID_FIELDS)[number],
  number
>;

export interface InvoiceBulkMarkPaidResult {
  appliedTotal: number;
  batchId: string;
  settledCount: number;
  skippedCount: number;
}

function bulkTargetRpcArgs(target: InvoiceBulkTarget): Record<string, unknown> {
  return {
    ...invoiceFilterRpcArgs(target.filters),
    p_invoice_ids: target.invoiceIds,
  };
}

/**
 * What a bulk mark-paid would cover and cost, computed by the server over the
 * whole target set.
 *
 * The confirm step is required to state both figures, and neither can come
 * from the page: "settle all matching" reaches records that were never
 * rendered, so a dollar amount summed from the rows in memory would understate
 * what the operator is about to authorize.
 */
export function previewInvoiceBulkMarkPaid(
  target: InvoiceBulkTarget,
): Promise<InvoiceBulkMarkPaidPreview> {
  return fetchListTotals(
    "invoice_bulk_mark_paid_preview",
    bulkTargetRpcArgs(target),
    INVOICE_BULK_MARK_PAID_FIELDS,
  );
}

/**
 * Settles every invoice in the target set, in one server-side transaction.
 *
 * `confirmedCount` and `confirmedBalance` are the figures the operator was
 * shown, handed back so the server can refuse the write if either has moved
 * since. That check is the reason this takes them at all — without it a
 * confirm dialog is a description of the past, not a guarantee about the write.
 *
 * There is no client-side loop here by design. N separate writes can fail
 * halfway and leave a batch that no one can reconcile; the RPC applies all of
 * them or none.
 */
export async function invoiceBulkMarkPaid({
  confirmedBalance,
  confirmedCount,
  date,
  method,
  reference,
  target,
}: {
  confirmedBalance: number;
  confirmedCount: number;
  date?: string | null;
  method: string;
  reference?: string | null;
  target: InvoiceBulkTarget;
}): Promise<InvoiceBulkMarkPaidResult> {
  const { data, error } = await backend.data.rpc("invoice_bulk_mark_paid", {
    ...bulkTargetRpcArgs(target),
    p_date: normalizeDateOnlyInput(date),
    p_expected_balance: confirmedBalance,
    p_expected_count: confirmedCount,
    p_method: method,
    p_reference: reference?.trim() || null,
  });

  if (error) {
    throw new Error(error.message);
  }

  const row = firstRecord(data);
  const batchId = row ? getOptionalString(row, "batch_id") : null;
  const appliedTotal = toNumber(row?.applied_total);
  const settledCount = toNumber(row?.settled_count);

  // The batch either reports what it did or it is not a result we can show.
  // Announcing "settled" off an unreadable response would tell an operator
  // money moved without knowing whether it did.
  if (!batchId || appliedTotal === null || settledCount === null) {
    throw new Error(
      "Bulk settlement returned an unreadable result. Check the invoice list before retrying.",
    );
  }

  return {
    appliedTotal,
    batchId,
    settledCount,
    skippedCount: toNumber(row?.skipped_count) ?? 0,
  };
}
